import apiClient from './apiClient';
import { ApiResponse } from '../types/api';

/**
 * Member Cost Service
 * 
 * Handles fetching member costs with support for historical rates
 * based on applicable_from dates in the member_salaries table.
 */

export interface MemberCosts {
  [userId: string]: {
    [month: string]: number; // month in format YYYY-MM, cost in INR
  };
}

export interface CostHistory {
  applicable_from: string;
  hourly_cost: number;
  monthly_salary: number;
  yearly_salary: number;
}

export class MemberCostService {
  /**
   * Get hourly costs for multiple members across multiple months.
   * 
   * Automatically uses the correct per_hour_cost based on applicable_from dates.
   * 
   * Example:
   *   Input: member_ids=[1], months=["2025-10", "2025-11", "2026-01"]
   *   Output: { "1": { "2025-10": 100, "2025-11": 100, "2026-01": 200 } }
   * 
   * Costs depend only on the member and month, so no project is involved and
   * opportunities use this too.
   *
   * @param memberIds - Array of user IDs
   * @param months - Array of months in YYYY-MM format
   * @returns Promise with costs mapped by user ID and month
   */
  static async getMemberCostsByMonths(
    memberIds: number[],
    months: string[]
  ): Promise<MemberCosts> {
    try {
      const response = await apiClient.post<ApiResponse<MemberCosts>>(
        '/members/costs',
        {
          member_ids: memberIds,
          months: months
        }
      );
      return response.data.data;
    } catch (error) {
      console.error('Error fetching member costs by months:', error);
      throw error;
    }
  }

  /**
   * Get cost change history for a specific member within a date range.
   * 
   * Useful for displaying rate changes to the user.
   * 
   * @param projectId - Project ID
   * @param userId - User ID
   * @param startDate - Start date (YYYY-MM-DD)
   * @param endDate - End date (YYYY-MM-DD)
   * @returns Promise with array of cost history records
   */
  static async getMemberCostHistory(
    projectId: number,
    userId: number,
    startDate: string,
    endDate: string
  ): Promise<CostHistory[]> {
    try {
      const response = await apiClient.get<ApiResponse<CostHistory[]>>(
        `/projects/${projectId}/members/${userId}/cost-history`,
        {
          params: {
            start_date: startDate,
            end_date: endDate
          }
        }
      );
      return response.data.data;
    } catch (error) {
      console.error('Error fetching member cost history:', error);
      throw error;
    }
  }

  /**
   * Calculate total cost for a member across multiple months.
   * 
   * @param userId - User ID
   * @param memberCosts - Costs object from getMemberCostsByMonths
   * @param monthlyHours - Object mapping month to hours allocated
   * @returns Total cost in INR
   */
  static calculateMemberTotalCost(
    userId: number,
    memberCosts: MemberCosts,
    monthlyHours: { [month: string]: number }
  ): number {
    const userIdStr = userId.toString();
    const userCosts = memberCosts[userIdStr];
    
    if (!userCosts) return 0;

    let total = 0;
    for (const [month, hours] of Object.entries(monthlyHours)) {
      const hourlyCost = userCosts[month] || 0;
      total += hourlyCost * hours;
    }

    return total;
  }

  /**
   * Get a breakdown of costs by month for a member.
   * 
   * @param userId - User ID
   * @param memberCosts - Costs object from getMemberCostsByMonths
   * @param monthlyHours - Object mapping month to hours allocated
   * @returns Array of monthly cost breakdowns
   */
  static getMemberCostBreakdown(
    userId: number,
    memberCosts: MemberCosts,
    monthlyHours: { [month: string]: number }
  ): Array<{
    month: string;
    hours: number;
    hourlyCost: number;
    totalCost: number;
  }> {
    const userIdStr = userId.toString();
    const userCosts = memberCosts[userIdStr];
    
    if (!userCosts) return [];

    const breakdown = [];
    for (const [month, hours] of Object.entries(monthlyHours)) {
      const hourlyCost = userCosts[month] || 0;
      breakdown.push({
        month,
        hours,
        hourlyCost,
        totalCost: hourlyCost * hours
      });
    }

    return breakdown;
  }
}

export default MemberCostService;

